// Upload-time model, shared by the estimator on /web-uploader (src/components/webUploader/UploadEstimator)
// and the web uploader's time comparison. Pure — every number here is an ESTIMATE and is labelled so.
//
// Two ways a gallery goes live:
//  - previews first (every plan, chosen per upload): a light copy of every photo goes up first; the gallery is live
//    when the copies are up, and the originals follow;
//  - originals only (the default — "Upload full size now"): live when the last
//    original is up.
//
// Previews. A copy's size comes from the uploader's own resize (PREVIEW_EDGE at PREVIEW_QUALITY,
// src/lib/uploader/previewCopy.ts): ≈150 KB by the size guess the upload manager uses. Calibrated against
// the owner's measurement — 10,000 photos live on previews in about 8 minutes on a 20 Mbps upload
// (17 Oct 2026) — at a realistic 90% use of the connection, that is ≈108 KB a copy on the wire (real
// event photos at 1200px q75 come in under the guess). The preview pass is also bounded by how fast the
// computer makes the copies: ~40–50 a second on an 8-core machine (staging bench, see
// src/lib/image/resizeImage.ts / useUploadManager), so ~5 a second per core, at most 8 cores (the pool cap).
//
// Originals are throughput-bound: their bytes over the connection at a realistic 85% use.
// No FTP figures: FTP apps' speeds haven't been measured.
import { PREVIEW_EDGE, PREVIEW_QUALITY, clampPreviewSize, estimatedCopyBytes, type PreviewSize } from "./previewCopy";

/** The owner's measurement the preview model is calibrated on. */
export const MEASURED = { photos: 10_000, mbps: 20, seconds: 8 * 60 } as const;

/** Share of the connection each pass realistically uses. */
export const PREVIEW_UTILISATION = 0.9;
export const ORIGINALS_UTILISATION = 0.85;

/** The preview copy's size by the upload manager's own size guess (≈150 KB). */
export const PREVIEW_BYTES_GUESS = estimatedCopyBytes(PREVIEW_EDGE, PREVIEW_QUALITY);

/** Bytes per preview copy on the wire, calibrated so the model reproduces MEASURED (≈108 KB). */
export const PREVIEW_BYTES = (MEASURED.seconds * ((MEASURED.mbps * 1e6) / 8) * PREVIEW_UTILISATION) / MEASURED.photos;

/** Bytes per preview copy on the wire at a chosen preview size: the calibrated default scaled by the size guess
 *  (so 1200px q75 is exactly PREVIEW_BYTES). Clamped to the allowed range. */
export const previewBytesFor = (preview?: Partial<PreviewSize> | null): number => {
  if (!preview) return PREVIEW_BYTES;
  const p = clampPreviewSize(preview);
  return PREVIEW_BYTES * (estimatedCopyBytes(p.maxEdge, p.quality) / PREVIEW_BYTES_GUESS);
};

/** Preview copies a second per core, and the most cores the resize pool uses. */
export const PREVIEWS_PER_SECOND_PER_CORE = 5;
export const MAX_RESIZE_CORES = 8;
/** Cores assumed when the device doesn't say (and for the page's example table). */
export const DEFAULT_CORES = 8;

/** A typical event JPEG original, in MB (decimal), editable in the estimator. */
export const DEFAULT_ORIGINAL_MB = 10;

export const PHOTO_PRESETS = [1_000, 5_000, 10_000, 25_000, 50_000, 100_000] as const;
export const EXAMPLE_SPEEDS_MBPS = [20, 50, 100, 500] as const;

export type EstimateInput = {
  photos: number;
  /** Total bytes of the originals (the uploader knows them exactly; the page uses photos × average). */
  originalBytes: number;
  /** Upload speed, megabits per second (decimal). */
  mbps: number;
  /** CPU cores (navigator.hardwareConcurrency); DEFAULT_CORES when unknown. */
  cores?: number;
  /** The preview copy's size (long edge / quality); the default 1200px q75 when omitted. */
  preview?: PreviewSize;
};

export type Estimate = {
  /** Previews first: seconds until every preview copy is up (the gallery can go live). */
  liveWithPreviews: number;
  /** Previews first: seconds until every original is up too (previews, then originals). */
  allOriginalsWithPreviews: number;
  /** Originals only: seconds until the gallery is live (= every original is up). */
  liveOriginalsOnly: number;
  /** Whether the preview pass is limited by the computer making copies rather than the connection. */
  previewsComputerBound: boolean;
};

const bytesPerSecond = (mbps: number) => (mbps * 1e6) / 8;

export const previewsPerSecond = (cores?: number): number => {
  const c = cores && cores > 0 ? cores : DEFAULT_CORES;
  return PREVIEWS_PER_SECOND_PER_CORE * Math.max(2, Math.min(MAX_RESIZE_CORES, c));
};

/** The estimate, in seconds — or null when an input is missing or not a positive number. */
export const estimateUpload = ({ photos, originalBytes, mbps, cores, preview }: EstimateInput): Estimate | null => {
  if (!(photos > 0) || !(originalBytes > 0) || !(mbps > 0) || !isFinite(photos + originalBytes + mbps)) return null;
  const bps = bytesPerSecond(mbps);
  const previewNet = (photos * previewBytesFor(preview)) / (bps * PREVIEW_UTILISATION);
  const previewCpu = photos / previewsPerSecond(cores);
  const liveWithPreviews = Math.max(previewNet, previewCpu);
  const originals = originalBytes / (bps * ORIGINALS_UTILISATION);
  return {
    liveWithPreviews,
    allOriginalsWithPreviews: liveWithPreviews + originals,
    liveOriginalsOnly: originals,
    previewsComputerBound: previewCpu > previewNet,
  };
};

/** A duration rounded the honest way for display: to the nearest minute under 10 min, nearest 5 min
 *  under an hour, nearest half hour under 10 h, nearest hour under 2 days, then nearest half day. */
export type RoundedDuration = { unit: "under" | "min" | "h" | "d"; n: number };

export const roundDuration = (seconds: number): RoundedDuration => {
  if (!(seconds > 0) || !isFinite(seconds)) return { unit: "under", n: 1 };
  if (seconds < 60) return { unit: "under", n: 1 };
  const min = seconds / 60;
  if (min < 9.5) return { unit: "min", n: Math.max(1, Math.round(min)) };
  const min5 = Math.round(min / 5) * 5;
  if (min5 < 60) return { unit: "min", n: min5 };
  const h = seconds / 3600;
  if (h < 9.75) return { unit: "h", n: Math.max(1, Math.round(h * 2) / 2) };
  if (h < 47.5) return { unit: "h", n: Math.round(h) };
  return { unit: "d", n: Math.round((h / 24) * 2) / 2 };
};

/** Mbps from upload progress samples [{t ms, bytes}], skipping the first `warmupMs` (connection set-up,
 *  slow start) when there's enough after it. Null without two usable samples. */
export const mbpsFromSamples = (samples: { t: number; bytes: number }[], warmupMs = 1500): number | null => {
  if (samples.length < 2) return null;
  const start = samples[0].t;
  const end = samples[samples.length - 1];
  const after = samples.find((s) => s.t - start >= warmupMs);
  const from = after && end.t - after.t >= 1000 ? after : samples[0];
  const dt = end.t - from.t;
  if (dt <= 0 || end.bytes <= from.bytes) return null;
  return ((end.bytes - from.bytes) * 8) / (dt / 1000) / 1e6;
};

/** Mbps rounded for display and for the estimator's input: one decimal under 10, whole above. */
export const roundMbps = (mbps: number): number => (mbps < 10 ? Math.round(mbps * 10) / 10 : Math.round(mbps));

/** The uploader's comparison is only worth showing when previews first saves at least this much. */
export const MIN_PREVIEW_FIRST_SAVING_S = 15 * 60;

export type PreviewFirstComparison = {
  /** Previews first: seconds until the gallery can go live. */
  previewsLive: number;
  /** Originals only: seconds until the gallery is live (every original up). */
  originalsLive: number;
  /** originalsLive − previewsLive. */
  savingS: number;
  /** Whether the saving is big enough to show (MIN_PREVIEW_FIRST_SAVING_S). */
  show: boolean;
};

/** Previews first vs originals only, for a real selection (exact count and bytes of the originals). */
export const comparePreviewFirst = (input: EstimateInput): PreviewFirstComparison | null => {
  const e = estimateUpload(input);
  if (!e) return null;
  const savingS = e.liveOriginalsOnly - e.liveWithPreviews;
  return { previewsLive: e.liveWithPreviews, originalsLive: e.liveOriginalsOnly, savingS, show: savingS >= MIN_PREVIEW_FIRST_SAVING_S };
};
